// BOHEMIA PURCHASE CONTROLLER.
//
// DO NOT DELETE THIS FILE BECAUSE OF ITS NAME. It is named bohemia-offer-pill.js for
// historical reasons only: it used to also carry a small Klaviyo offer-pill popup, which was
// removed on 2026-08-28 when the store moved to Klaviyo's own popup. What remains is the
// add-to-cart controller that owns every purchase on the Terra sections - BCB, Kay's Glow,
// Infinite Aura and The System. Removing it breaks every Add to cart on the core PDPs.
//
// layout/theme.liquid loads it. The sticky bars hand their clicks to it. See LOG.md
// section 2 (The Appstle problem) and 'Purchase surfaces' before changing anything here.

(() => {
  'use strict';

  const VERSION = '1.2.1';
  const TARGET_SELECTOR = [
    '[data-kay-glow-ads]',
    '[data-glow26]',
    '.terra-field-section',
    '.terra-system-vnext-product-section'
  ].join(',');
  const PURCHASE_SCOPE_SELECTOR = [
    '[data-kay-glow-ads]',
    '[data-glow26]',
    '.terra-field-section',
    '.terra-guide-section',
    '.terra-system-section',
    '.terra-purchase-marker-section',
    '.terra-system-vnext-product-section',
    '.terra-system-vnext-details-section',
    '.terra-system-individuals-section'
  ].join(',');

  const targetRoot = document.querySelector(TARGET_SELECTOR);
  // Install delegated handlers even when this deferred asset executes before a Terra section is present.
  // The paid landing page still owns its own cart flow.
  if (targetRoot?.matches('[data-kay-glow-ads]')) return;

  document.documentElement.dataset.bohemiaPurchaseController = VERSION;
  window.BohemiaPurchaseController = Object.freeze({ version: VERSION });
  const busyForms = new WeakSet();
  const shopRoot = window.Shopify?.routes?.root || '/';
  const route = (path) => `${shopRoot.replace(/\/$/, '')}${path}`;
  const money = (cents) => window.Shopify?.formatMoney
    ? window.Shopify.formatMoney(Number(cents))
    : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(cents) / 100);

  // WHY THIS USES THE PAGE'S OWN window.fetch, 2026-08-29.
  //
  // This controller used to post /cart/add through an unpatched fetch lifted out of a same-origin
  // iframe, to stop Appstle rewriting selling_plan. It worked, and it cost every pixel on the
  // store: that same bypass is invisible to SHOPIFY's instrumentation, which is what feeds the
  // web pixels. Measured on live V7: adds from the Kay's and Infinite Aura PDPs produced no
  // AddToCart at all, and BCB reported the $1.99 free Bamboo Spatula instead of the $39.99 balm,
  // because only Kaching's gift auto-add went through the normal path.
  //
  // The bypass was never the thing protecting the plan. snippets/terra-2026-purchase-card.liquid
  // keeps Appstle's own radio in lock-step with the customer's choice and re-asserts it in a
  // capture-phase handler immediately before submit, so by the time Appstle rewrites selling_plan
  // it writes back the id the card already chose. That is the sanctioned approach and the only
  // one Appstle's own docs support - they publish no way to disable the interception.
  //
  // So the add goes through window.fetch, Shopify sees it, every pixel fires, and repairLine()
  // below is the safety net for the case the lock-step ever loses the race.

  const requestJson = async (url, options = {}) => {
    const response = await window.fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) }
    });
    let payload = {};
    try { payload = await response.json(); } catch (error) { payload = {}; }
    if (!response.ok) throw new Error(payload.description || payload.message || 'The cart could not be updated. Please try again.');
    return payload;
  };

  const dispatchLoading = (name) => {
    document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true }));
  };

  const updateCartCounts = (cart) => {
    if (!cart || typeof cart.item_count !== 'number') return;
    document.querySelectorAll('[data-kgad-cart-count]').forEach((node) => { node.textContent = `(${cart.item_count})`; });
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = String(cart.item_count); });
  };

  const openVisibleCart = (source) => {
    document.dispatchEvent(new CustomEvent('kaching-cart:open', {
      detail: { element: source || document.documentElement, cartOpenSource: 'add-to-cart' }
    }));
  };

  // Ask Kaching to re-read /cart.js: once now, once again after its auto-add gift rule (Bamboo
  // Spatula) has had time to land. This used to be strictly required because the add bypassed the
  // page's patched fetch and Kaching never saw it. The add now goes through window.fetch, so
  // Kaching may pick it up on its own, but the second read is what makes the free gift appear
  // without a manual refresh. Keep it.
  const refreshKachingCart = async () => {
    const api = window.kachingCartApi;
    if (!api || typeof api.refreshCart !== 'function') return;
    try { await api.refreshCart(); } catch (error) {}
    window.setTimeout(() => { try { api.refreshCart(); } catch (error) {} }, 2500);
  };

  const statusNodeFor = (form) => {
    if (!form) return null;
    return form.querySelector('[data-cart-status], [data-kgad-status], [data-aura-status], [role="status"]')
      || form.closest('[data-glow26], [data-kay-glow-ads], [data-terra-field-root], [data-ts-product], .terra-system, .ts-details')?.querySelector('[data-cart-status], [data-kgad-status], [data-aura-status], [role="status"]');
  };

  const setBusy = (form, busy, submitter) => {
    if (!form) return;
    const buttons = new Set([
      submitter,
      ...form.querySelectorAll('button[type="submit"], input[type="submit"]')
    ].filter(Boolean));
    buttons.forEach((button) => {
      button.disabled = busy;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
    if (busy) busyForms.add(form);
    else busyForms.delete(form);
  };

  const formPayload = (formOrData) => {
    const data = formOrData instanceof FormData ? formOrData : new FormData(formOrData);
    const id = Number(data.get('id'));
    const quantity = Math.max(1, Number(data.get('quantity') || 1));
    const planValue = data.get('selling_plan');
    const payload = { id, quantity };
    if (planValue) payload.selling_plan = Number(planValue);
    if (!Number.isFinite(id) || id <= 0) throw new Error('Please choose an available product option.');
    return payload;
  };

  const authoritativeFormData = (form) => {
    const data = new FormData(form);
    const guideChoice = form.matches('.terra-guide__pair')
      ? form.querySelector('[data-terra-guide-purchase]:checked')
      : null;
    if (guideChoice) {
      data.delete('selling_plan');
      if (guideChoice.value) data.set('selling_plan', guideChoice.value);
    }
    // Terra PDP card: one hidden field carries the customer's choice. Disabled means one-time.
    const terraPlan = form.querySelector('input[data-terra-selling-plan]');
    if (terraPlan) {
      data.delete('selling_plan');
      if (!terraPlan.disabled && terraPlan.value) data.set('selling_plan', terraPlan.value);
    }
    return data;
  };

  const PROMO_VARIANT_IDS = new Set(['46560949534977', '46560949567745']);
  const promoHost = () => document.querySelector('[data-glow26], [data-kay-glow-ads]');
  // The code comes off the page, never a literal (TSK95). A campaign that ends must be able to
  // end by blanking one theme setting, without leaving a dead code compiled into the asset.
  const promoCode = () => (promoHost()?.dataset.discountCode || '').trim();
  const isPromoPage = () => Boolean(promoHost()) && Boolean(promoCode());
  const isPromoEligible = (payload) => isPromoPage()
    && !payload.selling_plan
    && PROMO_VARIANT_IDS.has(String(payload.id));

  // Returns whether the code was accepted. Never throws: the item is already in the cart, and
  // blocking checkout to protect a discount is backwards.
  const applyPromoCode = async () => {
    const code = promoCode();
    if (!code) return false;
    // Codes are owned by assets/bohemia-offer-code.js and live on the cart, never in a /discount
    // link (HARD RULE, 18 Sep 2026). No template activates this path today; it defers to the module.
    try {
      if (window.BohemiaOffer && typeof window.BohemiaOffer.ensure === 'function') {
        const cart = await window.BohemiaOffer.ensure();
        return Boolean(cart && (cart.discount_codes || []).some((d) => String(d.code).toUpperCase() === code.toUpperCase()));
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const findAddedLine = (cart, payload) => {
    const candidates = (cart.items || []).filter((line) => Number(line.variant_id) === Number(payload.id));
    return candidates.find((line) => payload.selling_plan
      ? Number(line.selling_plan_allocation?.selling_plan?.id) === Number(payload.selling_plan)
      : !line.selling_plan_allocation) || null;
  };

  const validateAddedLine = (cart, payload) => {
    const line = findAddedLine(cart, payload);
    if (!line) throw new Error('The selected purchase option was not added. Please try again.');
    if (payload.selling_plan && !line.selling_plan_allocation) {
      throw new Error('Auto-Refill was not attached to this item. Please try again.');
    }
    if (!payload.selling_plan && line.selling_plan_allocation) {
      throw new Error('The item was not added as a one-time purchase. Please try again.');
    }
    return line;
  };

  // Whether the offer actually landed on this line. Read, never enforced: an unapplied discount
  // is a worse price, not a broken cart, and it must not stop the customer (TSK95).
  const lineHasPromo = (line) => {
    const original = Number(line.original_line_price ?? line.original_price ?? 0);
    const finalPrice = Number(line.final_line_price ?? line.final_price ?? original);
    const hasAllocation = Array.isArray(line.discount_allocations) && line.discount_allocations.length > 0;
    const hasDiscount = Array.isArray(line.discounts) && line.discounts.length > 0;
    return finalPrice < original || hasAllocation || hasDiscount;
  };

  const getVerifiedCart = async (payload) => {
    let lastError = new Error('The selected purchase option was not added. Please try again.');
    for (let attempt = 0; attempt < 6; attempt += 1) {
      if (attempt) await new Promise((resolve) => window.setTimeout(resolve, 160 * attempt));
      const cart = await requestJson(route('/cart.js?bohemia_verify=' + Date.now()), { cache: 'no-store' });
      try {
        const line = validateAddedLine(cart, payload);
        return { cart, line };
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  };

  // Safety net. If Appstle still managed to write a plan we did not ask for - or strip one we did
  // - fix that line in place with /cart/change.js instead of throwing an error at the customer.
  // Returns true only when it actually changed something, so the caller knows to re-verify.
  const repairLine = async (payload) => {
    const cart = await requestJson(route('/cart.js?bohemia_repair=' + Date.now()), { cache: 'no-store' });
    const line = (cart.items || []).find((item) => Number(item.variant_id) === Number(payload.id));
    if (!line) return false;
    const want = payload.selling_plan ? String(payload.selling_plan) : '';
    const have = String(line.selling_plan_allocation?.selling_plan?.id || '');
    if (have === want) return false;
    await requestJson(route('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: line.key, quantity: line.quantity, selling_plan: want })
    });
    return true;
  };

  const addItem = async (payload, source, formData = null) => {
    const promo = isPromoEligible(payload);
    dispatchLoading('theme:loading:start');
    try {
      const pending = source?.matches?.('[data-astra-form]') ? source.astraPendingOffer : null;
      const retryOffer = pending && Number(pending.id) === Number(payload.id) && String(pending.selling_plan || '') === String(payload.selling_plan || '');
      if (!retryOffer) {
      if (payload.selling_plan) {
        const subscriptionData = formData instanceof FormData ? formData : new FormData();
        subscriptionData.set('id', String(payload.id));
        subscriptionData.set('quantity', String(payload.quantity));
        subscriptionData.set('selling_plan', String(payload.selling_plan));
        await requestJson(route('/cart/add.js?selling_plan=' + encodeURIComponent(String(payload.selling_plan))), {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: subscriptionData
        });
      } else {
        if (formData instanceof FormData) formData.delete('selling_plan');
        const addOptions = formData instanceof FormData
          ? { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: formData }
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ items: [payload] })
            };
        await requestJson(route('/cart/add.js'), addOptions);
      }
      }
      if (promo) await applyPromoCode();
      if (source?.matches?.('[data-astra-form]') && typeof source.astraApplyOffer === 'function') {
        await source.astraApplyOffer(payload);
      }
      let verified;
      try {
        verified = await getVerifiedCart(payload);
      } catch (error) {
        // One repair attempt, then verify again. If it still fails the original error stands.
        let repaired = false;
        try { repaired = await repairLine(payload); } catch (repairError) { repaired = false; }
        if (!repaired) throw error;
        verified = await getVerifiedCart(payload);
      }
      const cart = verified.cart;
      const line = verified.line;
      updateCartCounts(cart);
      cart.sections = cart.sections || {};
      await refreshKachingCart();
      source?.dispatchEvent?.(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: [line], cart, onSuccessDo: 'force_open_drawer' }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart, onSuccessDo: 'force_open_drawer' }
      }));
      openVisibleCart(source);
      document.dispatchEvent(new CustomEvent('bohemia:cart-updated', {
        detail: { cart, line, source, controllerVersion: VERSION }
      }));
      return { cart, line };
    } finally {
      dispatchLoading('theme:loading:end');
    }
  };

  const successMessage = (line, payload) => {
    const finalPrice = Number(line.final_price ?? line.final_line_price ?? 0);
    if (payload.selling_plan) return `Auto-Refill added at ${money(finalPrice)}.`;
    // Claim the offer only when the cart actually shows it. Saying "with the 20% offer" over a
    // full-price line is the same defect as blocking the sale, told as a lie instead (TSK95).
    if (isPromoEligible(payload) && lineHasPromo(line)) return `One-time purchase added at ${money(finalPrice)} with the 20% offer.`;
    return 'Added to cart.';
  };

  const purchaseFromForm = async (form, submitter) => {
    if (!form || busyForms.has(form)) return;
    const status = statusNodeFor(form);
    setBusy(form, true, submitter);
    if (status) status.textContent = 'Adding your selection...';
    try {
      const purchaseData = authoritativeFormData(form);
      const payload = formPayload(purchaseData);
      const result = await addItem(payload, form, purchaseData);
      if (status) status.textContent = successMessage(result.line, payload);
    } catch (error) {
      if (status) status.textContent = error.message || 'The cart could not be updated. Please try again.';
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error.message } }));
    } finally {
      setBusy(form, false, submitter);
    }
  };

  const shouldOwnSubmit = (form, submitter) => {
    if (!(form instanceof HTMLFormElement) || !form.closest(PURCHASE_SCOPE_SELECTOR)) return false;
    if (!form.matches('form[action*="/cart/add"], form[action$="/cart/add"], [data-kgad-form], [data-purchase-form], [data-aura-form], [data-terra-product-form-2026], .terra-system__form, .ts-details__purchase-form, .ts-product__form')) return false;
    if (submitter?.closest?.('.shopify-payment-button')) return false;
    if (submitter && submitter.name !== 'add' && !submitter.matches('[data-kgad-add], [data-purchase-submit], [data-aura-submit], [data-terra-add], [data-terra-system-add], .ts-product__submit, .ts-product__sticky-button, .ts-details__purchase-submit')) return false;
    if (!submitter && !form.matches('[data-kgad-form], [data-purchase-form], [data-aura-form]')) return false;
    return true;
  };

  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!shouldOwnSubmit(form, event.submitter)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    purchaseFromForm(form, event.submitter);
  }, true);

  window.addEventListener('click', (event) => {
    const stickySubmitter = event.target.closest?.('.ts-product__sticky-button');
    if (stickySubmitter?.closest(PURCHASE_SCOPE_SELECTOR)) {
      const stickyFormId = stickySubmitter.getAttribute('form');
      const stickyForm = (stickyFormId && document.getElementById(stickyFormId))
        || stickySubmitter.closest('[data-ts-product]')?.querySelector('.ts-product__form');
      if (stickyForm) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const stickyChoice = stickySubmitter.dataset.terraPurchaseChoice;
        if (stickyChoice === 'subscription' || stickyChoice === 'one-time') {
          document.dispatchEvent(new CustomEvent('terra-native-purchase:select', { detail: { choice: stickyChoice, source: stickySubmitter } }));
        }
        purchaseFromForm(stickyForm, stickyForm.querySelector('[data-terra-add], [data-ts-submit], button[name="add"]') || stickySubmitter);
      }
      return;
    }

    const button = event.target.closest?.('[data-add-selected], [data-kgad-add-selected], [data-kgad-add-pairing], [data-terra-purchase-marker-add]');
    if (!button || !button.closest(PURCHASE_SCOPE_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    if (button.matches('[data-terra-purchase-marker-add]')) {
      const form = document.querySelector('[data-terra-product-form-2026]');
      const submitter = form?.querySelector('[data-terra-add], button[name="add"]');
      const choice = button.dataset.terraPurchaseChoice;
      if (choice === 'subscription' || choice === 'one-time') {
        // Synchronous: the purchase card switches its choice (and the hidden selling_plan) before the form is read.
        document.dispatchEvent(new CustomEvent('terra-native-purchase:select', { detail: { choice, source: button } }));
      }
      purchaseFromForm(form, submitter);
      return;
    }

    if (button.matches('[data-kgad-add-pairing]')) {
      if (button.getAttribute('aria-busy') === 'true') return;
      const root = button.closest('[data-kay-glow-ads]');
      const choice = root?.querySelector('.kgad__pair-purchase-option input:checked');
      const payload = { id: Number(button.dataset.variantId), quantity: 1 };
      if (choice?.value === 'refill' && button.dataset.sellingPlan) payload.selling_plan = Number(button.dataset.sellingPlan);
      const status = root?.querySelector('[data-kgad-pairing-status]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Adding your pair...';
      addItem(payload, button).then(({ line }) => {
        if (status) status.textContent = successMessage(line, payload);
      }).catch((error) => {
        if (status) status.textContent = error.message || 'The pair could not be added. Please try again.';
      }).finally(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
      return;
    }

    const root = button.closest('[data-glow26], [data-kay-glow-ads]');
    const form = root?.querySelector('[data-purchase-form], [data-kgad-form]');
    const submitter = form?.querySelector('[data-purchase-submit], [data-kgad-add]');
    purchaseFromForm(form, submitter);
  }, true);

  window.addEventListener('cart:change', (event) => {
    event.stopImmediatePropagation();
    const cart = event.detail?.cart;
    if (cart) updateCartCounts(cart);
    openVisibleCart(event.target);
  }, true);

  const syncSystemPurchaseState = (source = 'primary') => {
    const primary = document.querySelector('.ts-product__form');
    const details = document.querySelector('.ts-details__purchase-form');
    if (!primary || !details) return;
    const detailsPlan = details.querySelector('[data-ts-details-selling-plan-input]');
    const detailsOneTime = details.querySelector('[data-ts-details-plan="one-time"]');
    const detailsSubscription = details.querySelector('[data-ts-details-plan="subscription"]');

    // The primary form is either the shared purchase card ([data-terra-purchase-choice]) or the
    // older section-owned selector ([data-ts-purchase-input]).
    const sharedCard = primary.querySelector('[data-terra-native-purchase]');

    if (source === 'details') {
      const selected = details.querySelector('[data-ts-details-plan]:checked');
      const type = selected?.dataset.tsDetailsSellingPlan ? 'subscription' : 'one-time';
      if (sharedCard) {
        if (sharedCard.terraPurchase?.selectedChoice() !== type) {
          document.dispatchEvent(new CustomEvent('terra-native-purchase:select', { detail: { choice: type, source: details } }));
        }
        return;
      }
      const primaryChoice = primary.querySelector('[data-ts-purchase-input][value="' + type + '"]');
      if (primaryChoice && !primaryChoice.checked) {
        primaryChoice.checked = true;
        primaryChoice.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const primaryChoice = sharedCard
      ? sharedCard.querySelector('[data-terra-purchase-choice]:checked')
      : primary.querySelector('[data-ts-purchase-input]:checked');
    const subscription = primaryChoice?.value === 'subscription';
    const selectedDetails = subscription ? detailsSubscription : detailsOneTime;
    if (selectedDetails && !selectedDetails.checked) selectedDetails.checked = true;
    details.querySelectorAll('[data-ts-details-plan]').forEach((input) => {
      input.closest('.ts-details__purchase-option')?.classList.toggle('is-selected', input === selectedDetails);
    });

    if (detailsPlan) {
      const cardPlan = primary.querySelector('input[data-terra-selling-plan]:not(:disabled)');
      const selectedAppstlePlan = primary.querySelector('input[name="selling_plan"]:checked');
      const fallbackPlan = primary.querySelector('[data-ts-plan-fallback]:not(:disabled)');
      const planValue = subscription ? (cardPlan?.value || selectedAppstlePlan?.value || fallbackPlan?.value || detailsSubscription?.dataset.tsDetailsSellingPlan || '') : '';
      detailsPlan.disabled = !subscription;
      detailsPlan.value = planValue;
    }
  };

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('.ts-details__purchase-form [data-ts-details-plan]')) {
      window.setTimeout(() => syncSystemPurchaseState('details'), 0);
    }
    if (event.target.matches?.('.ts-product__form [name="selling_plan"]')) {
      window.setTimeout(() => syncSystemPurchaseState('primary'), 0);
    }
  }, true);

  ['appstleInit', 'SellingPlanSelected', 'SellingPlanDeSelected', 'sellingPlanChanged', 'terra-native-purchase:change'].forEach((name) => {
    document.addEventListener(name, () => window.setTimeout(() => syncSystemPurchaseState('primary'), 0));
  });

  const systemForm = document.querySelector('.ts-product__form');
  if (systemForm) {
    const observer = new MutationObserver(() => syncSystemPurchaseState('primary'));
    observer.observe(systemForm, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'value', 'disabled', 'class'] });
    window.setTimeout(() => syncSystemPurchaseState('primary'), 0);
  }

})();
